import React, { useMemo } from 'react';
import size from 'lodash/size';
import map from 'lodash/map';

import { IsFetched, ErrorMessage, ID } from '../../../../../types';

import {
  UpdateLifestylesSetCacheAction,
  FetchLifestylesSetsCacheKeys
} from '../../../../lifestylesSets/lifestylesSetsTypes';
import { LifestylesListOnLifestylesSelect } from '../../../../lifestyles/components/lists/LifestylesList';

import {
  FetchLifestylesCacheKeys,
  OnSelectedLifestylesSidebarCloseAction
} from '../../../../lifestyles/lifestylesTypes';
import { SelectedLifestylesSidebarLifestylesSet } from './SelectedLifestylesSidebar.types';

import { useTableCheckable } from '../../../../../common/hooks/useTableCheckable';
import { useCurrentUser } from '../../../../../auth/hooks/useAuth';

import { SelectedLibraryItemsSidebar } from '../../../../common/components/sidebars/SelectedLibraryItemsSidebar';

import {
  SelectedLifestylesList,
  SelectedLifestylesListLifestylesSetSelectedLifestyles
} from '../../lists/SelectedLifestylesList';

import { SelectedLifestylesSidebarFooter } from './components/SelectedLifestylesSidebarFooter';

import { SelectedLifestylesPermissions } from '../../../selectedLifestylesConstants';
import { lifestylesKeys } from '../../../../../locales/keys';

interface SelectedLifestylesSidebarProps {
  lifestylesSet: SelectedLifestylesSidebarLifestylesSet;
  lifestylesSetFetched: IsFetched;
  lifestylesSetError: ErrorMessage;
  lifestylesSetIsPlaceholderData: boolean;
  lifestylesCacheKeys?: FetchLifestylesCacheKeys;
  lifestylesSetCacheKeys?: FetchLifestylesSetsCacheKeys;
  onClose: OnSelectedLifestylesSidebarCloseAction;
  onCustomAttach?: LifestylesListOnLifestylesSelect;
  updateLifestylesSetCache: UpdateLifestylesSetCacheAction<SelectedLifestylesSidebarLifestylesSet>;
  withoutBackGround?: boolean;
  disabledAttach?: boolean;
}

function SelectedLifestylesSidebar({
  lifestylesSet,
  lifestylesSetFetched,
  lifestylesSetError,
  lifestylesSetIsPlaceholderData,
  lifestylesCacheKeys,
  lifestylesSetCacheKeys,
  onClose,
  onCustomAttach,
  updateLifestylesSetCache,
  withoutBackGround,
  disabledAttach
}: SelectedLifestylesSidebarProps) {
  const currentUser = useCurrentUser();

  const selectedLifestyles =
    useMemo<SelectedLifestylesListLifestylesSetSelectedLifestyles>(
      () => lifestylesSet?.selectedLifestyles || [],
      [lifestylesSet]
    );

  const defaultCheckedIds = useMemo<ID[]>(
    () => map(selectedLifestyles, 'id'),
    [selectedLifestyles]
  );

  const checkable = currentUser.hasPermissions(
    SelectedLifestylesPermissions.CHANGE_SELECTED_LIFESTYLES_CHECKED
  );

  const {
    checkedHash,
    checkedAll,
    handleSetCheckedIds,
    handleCheckAll,
    checkedItems
  } = useTableCheckable({
    items: selectedLifestyles,
    checkedIds: defaultCheckedIds
  });

  return (
    <SelectedLibraryItemsSidebar
      i18nTitle={lifestylesKeys.selected}
      onClose={onClose}
      footer={
        <SelectedLifestylesSidebarFooter
          lifestylesSet={lifestylesSet}
          checkedSelectedLifestyleIds={map(checkedItems, 'id')}
          onClose={onClose}
          onCustomAttach={onCustomAttach}
          disabledAttach={disabledAttach}
          updateLifestylesSetCache={updateLifestylesSetCache}
        />
      }
      selectedItemsCount={size(lifestylesSet.selectedLifestyles)}
      withoutBackGround={withoutBackGround}
    >
      <SelectedLifestylesList
        lifestylesSet={lifestylesSet}
        lifestylesSetFetched={lifestylesSetFetched}
        lifestylesSetError={lifestylesSetError}
        lifestylesSetIsPlaceholderData={lifestylesSetIsPlaceholderData}
        lifestylesCacheKeys={lifestylesCacheKeys}
        lifestylesSetCacheKeys={lifestylesSetCacheKeys}
        onSelectedLifestylesSidebarClose={onClose}
        updateLifestylesSetCache={updateLifestylesSetCache}
        checkable={checkable}
        checkedAll={checkedAll}
        checkedHash={checkedHash}
        onCheckAll={handleCheckAll}
        onSetCheckedIds={handleSetCheckedIds}
      />
    </SelectedLibraryItemsSidebar>
  );
}

export default SelectedLifestylesSidebar;
